import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Text, Button, Flex, Box, Dialog, Heading } from '@radix-ui/themes';
import { Slider } from 'primereact/slider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useStore } from '../../store/store';
import api from '../../services/api';
import { Message } from 'primereact/message';
import Icon from '@mdi/react';
import { mdiCreation } from '@mdi/js';
import avatar from '../../assets/avatar.png';

export const ChapterCreation = ({ novelId }) => {
    const navigate = useNavigate();
    const [state, actions] = useStore();
    
    const [chapterCount, setChapterCount] = useState(8);
    const [generatedChapters, setGeneratedChapters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const handleGenerateChapters = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await api.generateChapters(novelId, chapterCount);
            if (response.success) {
                setGeneratedChapters(response.chapters);
            } else {
                setError(response.error || 'Failed to generate chapters');
            }
        } catch (err) {
            console.error('Error generating chapters:', err);
            setError('An error occurred while generating chapters');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveChapters = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await api.saveChapters(novelId, generatedChapters);
            if (response.success) {
                setSaved(true);
                // Update the current novel in the store if needed
                if (state.currentNovel) {
                    const updatedNovel = { ...state.currentNovel, chapters: generatedChapters };
                    actions.setCurrentNovel(updatedNovel);
                }
            } else {
                setError(response.error || 'Failed to save chapters');
            }
        } catch (err) {
            console.error('Error saving chapters:', err);
            setError('An error occurred while saving chapters');
        } finally {
            setLoading(false);
        }
    };

    const handleContinue = () => {
        navigate(`/novel/${novelId}`);
    };

    return (
        <Box p="4" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading size="6" mb="4" style={{ fontFamily: 'Poppins' }}>Create Chapters</Heading>

            {state.novels.length === 0 ?
                <Flex className="tutorial-container" style={{}}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        <div>
                            This step involves a big decision: do you want to let the AI outline the entire novel for you, or do you want to write and grow it yourself?<br /><br /> If you choose AI, set the number of chapters, and the AI will generate the chapters and scene summaries (this will take some time) for the entire novel. All that is left is to go through the scenes and generate the content.<br /><br />
                            If you choose to write the novel yourself, you can skip this step ("Finish & Go TO Novel").
                            <br />
                        </div>
                    </Flex>
                </Flex> : null}

            {generatedChapters.length == 0 && <><Message severity="info" text="If you want to add chapters manually, just hit the finish button. Otherwise use the AI to outline the novel." /><br /><br /></>}
            <Card mb="4">
                <Flex direction="column" gap="3" p="3">
                    <Text as="label" size="2" weight="bold">
                        Number of chapters: {chapterCount}
                    </Text>
                    <Flex align="center" gap="3">
                        <Text size="2">3</Text>
                        <Slider
                            value={chapterCount}
                            onChange={(e) => setChapterCount(e.value)}
                            min={3}
                            max={15}
                            style={{ width: '100%', maxWidth: '400px' }}
                        />
                        <Text size="2">15</Text>
                    </Flex>
                    
                    <Button 
                        disabled={loading} 
                        onClick={handleGenerateChapters}
                        style={{background: '#6221ab', cursor: 'pointer', width: 'fit-content'}}
                        mt="2"
                    >   
                        <>
                        <Icon style={{transform: 'scale(0.8)'}} path={mdiCreation} size={1} />
                        Generate Chapters
                        </>
                    </Button>
                </Flex>
            </Card>

            {loading && (
                <Flex justify="center" mt="4" mb="4">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                </Flex>
            )}

            {error && (
                <Card color="red" mt="4" mb="4">
                    <Text>{error}</Text>
                </Card>
            )}

            {generatedChapters.length > 0 && !loading ? 
                <>
                    <Heading size="4" mt="4" mb="2">Generated Chapters</Heading>
                    <Card>
                        {generatedChapters.map((chapter, index) => (
                            <Box key={index} p="3" style={{ borderBottom: index < generatedChapters.length - 1 ? '1px solid #eee' : 'none' }}>
                                <Flex justify="between">
                                    <Text weight="bold">{chapter.chapter_number}. {chapter.title}</Text>
                                </Flex>
                            </Box>
                        ))}
                    </Card>

                    <Flex gap="3" mt="4" justify="end">
                        <Button 
                            disabled={loading || saved} 
                            onClick={handleSaveChapters}
                            color="blue"
                        >
                            {saved ? 'Saved' : 'Save Chapters'}
                        </Button>
                        
                        <Button 
                            onClick={handleContinue}
                            color="green"
                        >
                            {saved ? 'Continue to Novel' : 'Finish & Go To Novel'}
                        </Button>
                    </Flex>
                </>
            : <Button 
            onClick={handleContinue}
            color="green"
        >
            {saved ? 'Continue to Novel' : 'Finish & Go To Novel'}
        </Button>}

            {/* Success Dialog */}
            <Dialog.Root open={saved}>
                <Dialog.Content>
                    <Dialog.Title>Chapters Saved Successfully</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Your chapters have been saved. You can now continue to your novel.
                    </Dialog.Description>
                    <Flex justify="end">
                        <Button onClick={handleContinue}>
                            Continue
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>
        </Box>
    );
};

export default ChapterCreation;